/* eslint-disable max-lines-per-function */
import { pick, flatten } from "lodash";
import { FormAnswer, FormQuestionData } from "types/gql-types";
import { FormValues, Status, Upload } from "./type";
import { keyFromIdentity } from "./components/helpers";

const hasBlanks = (value: any) => [null, ""].includes(value);
const hasEmpty = (value: any) => {
  if (!value) return true;

  if (Array.isArray(value)) return value.length === 0;
  if (value instanceof Date) return false;

  if (typeof value === "object") return !Object.values(value).some(Boolean);

  return false;
};

const isAllStrings = (values: any) => values.every((value: any) => typeof value === "string");

// marshal returns a set of form answers suitable to be passed to GraphQL
// mutations. The values of each answer as JSON encoded.
export const marshalV2 = (
  questions: FormQuestionData[],
  values: FormValues[],
  contextID?: string,
  allowEmpty?: boolean,
):FormAnswer[] => {
  const questionsMap: Record<string, FormQuestionData> = questions.reduce((acc, question) =>
    ({ ...acc, [keyFromIdentity(question.identity)]: question }), {});

  return flatten(Object.entries(values).map(([key, value]) => {
    const isBlank = hasBlanks(value);
    const isEmpty = allowEmpty ? false : hasEmpty(value);

    if (isBlank || isEmpty) return false;

    const question = questionsMap[key];
    const [name, ...context] = key.split("/");

    if (question?.__typename === "FormQuestionUpload") {
      const stuff = (value as unknown as Array<Upload>)
        .filter((document) => document.status === Status.Finished)
        .map((document) => pick(document, ["id", "name"]));

      return {
        questionName: name,
        context,
        key: contextID,
        value: JSON.stringify(stuff),
      } as FormAnswer;
    }

    // eslint-disable-next-line no-warning-comments
    // TODO: isAllStrings is a bit of a work around for now; This needs revisited/cleaned up
    //   when switching this marshal over to using the question type specific formatters
    //
    // If it's an array but every element is a string then the value
    // is either a value for a multiple select question, or a set of
    // id's pointing to the child answers.
    if (Array.isArray(value) && !isAllStrings(value)) {
      const [parentValue, childValues] = value.reduce(
        ([parentValues, childrenValues], { uniqueID, ...keys }) => (
          [
            [...parentValues, uniqueID],
            [...childrenValues, ...marshalV2(questions, keys, uniqueID, allowEmpty)],
          ]
        ),
        [[], []],
      ) as [string[], FormAnswer[]];

      return [
        {
          questionName: name,
          context,
          key: contextID,
          value: JSON.stringify(parentValue),
        } as FormAnswer,
        ...childValues,
      ];
    }

    return {
      questionName: name,
      context,
      key: contextID,
      value: JSON.stringify(value),
    } as FormAnswer;
  })).filter(Boolean) as FormAnswer[];
};
